import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScGrid from '../ScGrid';
import ScModal from '../ScModal';
import ScSpinner from '../ScSpinner';
import { fetchEmailMappings, createEmailMapping, updateEmailMapping, fetchLookups } from '../../studyConfigurationApi';

const FIXED_OPERATION_CODE = '0606';

const defaultActivityNo = (activities) => {
  const list = activities || [];
  const general = list.find((a) => a.activityName === 'General');
  return general?.activityNo ?? list[0]?.activityNo ?? '';
};

const emptyForm = (lookups) => ({
  operationCode: FIXED_OPERATION_CODE,
  profileNo: lookups.profiles[0]?.profileNo ?? '',
  activityNo: defaultActivityNo(lookups.activities),
  emailFormatMstNo: '',
});

const EmailMappingTab = () => {
  const [rows, setRows] = useState([]);
  const [lookups, setLookups] = useState({ profiles: [], operations: [], activities: [], emailFormats: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm({ profiles: [] }));
  const [editId, setEditId] = useState(null);

  const fixedOperation = useMemo(
    () => (lookups.operations || []).find((o) => o.operationCode === FIXED_OPERATION_CODE),
    [lookups.operations]
  );

  const fixedOperationLabel = fixedOperation
    ? `${FIXED_OPERATION_CODE} — ${fixedOperation.operationName}`
    : FIXED_OPERATION_CODE;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, lookRes] = await Promise.all([fetchEmailMappings(), fetchLookups()]);
      setRows(listRes.data || []);
      setLookups(lookRes.data || {});
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load mappings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditId(null);
    setError(null);
    setForm(emptyForm(lookups));
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.emailActivityMappingNo);
    setError(null);
    setForm({
      operationCode: FIXED_OPERATION_CODE,
      profileNo: row.profileNo,
      activityNo: row.activityNo ?? '',
      emailFormatMstNo: row.emailFormatMstNo ?? '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (form.activityNo === '' || form.activityNo == null) {
      setError('Activity is required.');
      return;
    }
    if (form.emailFormatMstNo === '' || form.emailFormatMstNo == null) {
      setError('Email format is required.');
      return;
    }
    const payload = {
      ...form,
      operationCode: FIXED_OPERATION_CODE,
      activityNo: Number(form.activityNo),
      profileNo: Number(form.profileNo),
      emailFormatMstNo: Number(form.emailFormatMstNo),
    };
    try {
      if (editId) await updateEmailMapping(editId, { ...payload, emailActivityMappingNo: editId });
      else await createEmailMapping(payload);
      setModalOpen(false);
      setError(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    }
  };

  if (loading) return <ScSpinner />;
  return (
    <div>
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      <div className="sc-toolbar">
        <button
          type="button"
          className="sc-btn sc-btn-primary"
          onClick={openAdd}
          disabled={!lookups.activities?.length}
          title={!lookups.activities?.length ? 'Add an activity in the Activities tab first.' : undefined}
        >
          Add Mapping
        </button>
      </div>
      <ScGrid
        columns={[
          { key: 'operationCode', label: 'Operation' },
          { key: 'profileCode', label: 'Profile' },
          { key: 'activityName', label: 'Activity' },
          { key: 'emailFormat', label: 'Format' },
        ]}
        rows={rows}
        onEdit={openEdit}
      />
      <ScModal
        open={modalOpen}
        title={editId ? 'Edit Mapping' : 'Add Mapping'}
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button type="button" className="sc-btn sc-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="button" className="sc-btn sc-btn-primary" onClick={save}>Save</button>
          </>
        )}
      >
        <div className="sc-form-row">
          <label>Operation</label>
          <select value={FIXED_OPERATION_CODE} disabled aria-label="Operation">
            <option value={FIXED_OPERATION_CODE}>{fixedOperationLabel}</option>
          </select>
        </div>
        <div className="sc-form-row">
          <label>Profile</label>
          <select value={form.profileNo} onChange={(e) => setForm({ ...form, profileNo: e.target.value })}>
            {lookups.profiles.map((p) => (
              <option key={p.profileNo} value={p.profileNo}>{p.profileCode}</option>
            ))}
          </select>
        </div>
        <div className="sc-form-row">
          <label>Activity <span className="sc-required">*</span></label>
          <select
            value={form.activityNo}
            required
            onChange={(e) => setForm({ ...form, activityNo: e.target.value })}
          >
            <option value="">Select activity…</option>
            {lookups.activities.map((a) => (
              <option key={a.activityNo} value={a.activityNo}>{a.activityName}</option>
            ))}
          </select>
        </div>
        <div className="sc-form-row">
          <label>Email Format <span className="sc-required">*</span></label>
          <select
            value={form.emailFormatMstNo}
            required
            onChange={(e) => setForm({ ...form, emailFormatMstNo: e.target.value })}
          >
            <option value="">Select email format…</option>
            {lookups.emailFormats.map((f) => (
              <option key={f.emailFormatMstNo} value={f.emailFormatMstNo}>{f.emailFormat}</option>
            ))}
          </select>
        </div>
      </ScModal>
    </div>
  );
};

export default EmailMappingTab;
